import {
    getFacultyOrStudentActivities,
    getActivitySubmissions,
    createActivityRecord,
    closeActivityRecord,
    submitActivityRecord,
    reviewActivitySubmission,
    getCourseArtifactFile,
} from '../../../../Utils/apiService';
import { getMimeTypeForFileName } from '../../../../Utils/fileMimeTypes';

export interface ActivityItem {
    activityId: string;
    title: string;
    description: string;
    dueDate?: string;
    status: string;
    submittedCount: number;
    totalEnrolledCount: number;
    mySubmissionStatus?: string;
    mySubmissionId?: string;
    // The optional reference document/link attached at creation (createActivity's
    // fileName/base64Data) — isReferenceLink distinguishes a plain URL from an
    // uploaded file, only the latter has a referenceContentVersionId to fetch.
    referenceFileName?: string;
    referenceFileType?: string;
    referenceContentVersionId?: string;
    referenceUrl?: string;
    isReferenceLink?: boolean;
}

// Fully documented response shape (RestApi_Activities.cls) —
// { activities: [{activityId, title, description, dueDate, status,
// submittedCount, totalEnrolledCount, mySubmissionStatus, mySubmissionId,
// referenceUrl, referenceFileType, referenceFileName,
// referenceContentVersionId, isReferenceLink}] }.
// mySubmissionStatus/mySubmissionId are only present when role=Student.
// Confirmed live value for a not-yet-submitted activity: mySubmissionStatus
// is the literal string "Not Submitted" (not "Pending"), mySubmissionId is
// absent/undefined. Confirmed live: an activity created without a reference
// document returns referenceFileName/referenceFileType/
// referenceContentVersionId/isReferenceLink as null, not absent.
export const fetchActivities = async (
    batchId: string,
    role: 'Faculty' | 'Student',
    contactId?: string
): Promise<ActivityItem[]> => {
    if (!batchId) return [];
    try {
        const response = await getFacultyOrStudentActivities(batchId, role, contactId);
        const rawList: any[] = Array.isArray(response?.activities) ? response.activities : [];
        return rawList.map((item): ActivityItem => ({
            activityId: item.activityId,
            title: item.title,
            description: item.description,
            dueDate: item.dueDate || undefined,
            status: item.status,
            submittedCount: item.submittedCount ?? 0,
            totalEnrolledCount: item.totalEnrolledCount ?? 0,
            mySubmissionStatus: item.mySubmissionStatus || undefined,
            mySubmissionId: item.mySubmissionId || undefined,
            referenceFileName: item.referenceFileName || undefined,
            referenceFileType: item.referenceFileType || undefined,
            referenceContentVersionId: item.referenceContentVersionId || undefined,
            referenceUrl: item.referenceUrl || undefined,
            isReferenceLink: item.isReferenceLink ?? undefined,
        })).filter((a) => a.activityId);
    } catch (error) {
        console.error('Error fetching activities:', error);
        return [];
    }
};

export interface ActivitySubmission {
    submissionId: string;
    contactName: string;
    status: string;
    feedback?: string;
    contentVersionId?: string;
    fileName?: string;
}

// { submissions: [{submissionId, contactName, status, feedback,
// contentVersionId, fileName}] }
export interface ActivityProgressSummary {
    totalActivities: number;
    approvedActivities: number;
    progressPercent: number;
}

// Course progress for a student is driven by activity approvals, not lesson
// completion — an activity counts toward progress only once faculty marks
// its submission Approved via reviewSubmission (submitting alone isn't
// enough on its own).
export const fetchActivityProgress = async (
    batchId: string,
    contactId: string
): Promise<ActivityProgressSummary> => {
    const fallback: ActivityProgressSummary = { totalActivities: 0, approvedActivities: 0, progressPercent: 0 };
    if (!batchId || !contactId) return fallback;
    const items = await fetchActivities(batchId, 'Student', contactId);
    const totalActivities = items.length;
    const approvedActivities = items.filter((item) => item.mySubmissionStatus === 'Approved').length;
    return {
        totalActivities,
        approvedActivities,
        progressPercent: totalActivities > 0 ? Math.round((approvedActivities / totalActivities) * 100) : 0,
    };
};

export const fetchActivitySubmissions = async (activityId: string): Promise<ActivitySubmission[]> => {
    if (!activityId) return [];
    try {
        const response = await getActivitySubmissions(activityId);
        const rawList: any[] = Array.isArray(response?.submissions) ? response.submissions : [];
        return rawList.map((item): ActivitySubmission => ({
            submissionId: item.submissionId,
            contactName: item.contactName,
            status: item.status,
            feedback: item.feedback || undefined,
            contentVersionId: item.contentVersionId || undefined,
            fileName: item.fileName || undefined,
        })).filter((s) => s.submissionId);
    } catch (error) {
        console.error('Error fetching activity submissions:', error);
        return [];
    }
};

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

// Faculty-side authoring — thin wrappers, Faculty-only per the API docs.
// The reference document is optional; createActivity works with or without one.
export const createActivity = async (input: {
    contactId: string;
    batchId: string;
    title: string;
    description: string;
    dueDate?: string;
    file?: File;
}) => {
    const { file, ...rest } = input;
    if (!file) return createActivityRecord(rest);
    const base64Data = await readFileAsBase64(file);
    return createActivityRecord({ ...rest, fileName: file.name, base64Data });
};

export const closeActivity = async (input: { contactId: string; activityId: string }) =>
    closeActivityRecord(input);

export const reviewSubmission = async (input: {
    contactId: string;
    submissionId: string;
    status: 'Reviewed' | 'Approved' | 'Rejected';
    feedback?: string;
}) => reviewActivitySubmission(input);

// Student-side submission.
export const submitActivity = async (input: {
    contactId: string;
    activityId: string;
    file: File;
}) => {
    const base64Data = await readFileAsBase64(input.file);
    return submitActivityRecord({
        contactId: input.contactId,
        activityId: input.activityId,
        fileName: input.file.name,
        base64Data,
    });
};

// Fetches a submission's raw file bytes — caller renders it (e.g. via
// FilePreviewDialog's useFilePreview hook) rather than this helper opening
// anything itself. Submissions carry a fileName but no contentType field —
// derive the MIME type from the extension.
export const fetchActivitySubmissionFile = async (submission: ActivitySubmission): Promise<{ buffer: ArrayBuffer; fileName: string; mimeType: string } | null> => {
    if (!submission.contentVersionId) return null;
    const buffer = await getCourseArtifactFile(submission.contentVersionId);
    const fileName = submission.fileName || 'file';
    const mimeType = getMimeTypeForFileName(fileName);
    return { buffer, fileName, mimeType };
};

// Fetches the reference document faculty attached when creating the
// activity (createActivity's optional file). Only present when
// isReferenceLink is false and a referenceContentVersionId came back — a
// plain reference link (isReferenceLink true) has nothing to fetch here,
// the caller should just open the referenceUrl directly.
export const fetchActivityReferenceFile = async (activity: {
    referenceContentVersionId?: string;
    referenceFileName?: string;
    isReferenceLink?: boolean;
}): Promise<{ buffer: ArrayBuffer; fileName: string; mimeType: string } | null> => {
    if (!activity.referenceContentVersionId || activity.isReferenceLink) return null;
    const buffer = await getCourseArtifactFile(activity.referenceContentVersionId);
    const fileName = activity.referenceFileName || 'file';
    const mimeType = getMimeTypeForFileName(fileName);
    return { buffer, fileName, mimeType };
};
